import * as React from "react";
import { GeneralModal } from "../Common/GeneralModal/GeneralModal";
import { useLoader } from "../Common/GeneralLoader/GeneralLoaderProvider";
import { drupalSettings } from "../../utils/config";
import {
  bankTransferConfirmationApi,
  bankTransferApproveApi,
  bankTransferDenyApi,
} from "../../api/api";
import { AuthorizeConfirm } from "./Steps/AuthorizeConfirm";
import { DenyConfirm } from "./Steps/DenyConfirm";
import { SuccessStep } from "./Steps/SuccessStep";
import { ErrorStep } from "./Steps/ErrorStep";
import { ErrorInsufficientFundsStep } from "./Steps/ErrorInsufficientFundsStep";
import { RequestDeniedStep } from "./Steps/RequestDeniedStep";
import { ScaStep } from "./Steps/ScaStep";
import { InsufficientFundsQueueStep } from "./Steps/InsufficientFundsQueueStep";
import { QueuedAuthorizationSuccessStep } from "./Steps/QueuedAuthorizationSuccessStep";

/**
 * Steps:
 * 0 = loading confirmation
 * 1 = Authorize confirm (Are you sure? ... No/Yes)
 * 2 = SCA verification (after 403 from approve with session code)
 * 3 = Success
 * -1 = Error (general)
 * -2 = Error (insufficient funds)
 * 4 = Deny confirm
 * 5 = Request Denied success
 * 6 = Insufficient funds — queue authorization prompt
 * 7 = Queued for payment — success
 */
export function AuthoriseBankTransfer({ orderId, mode = "authorise" }) {
  const [step, setStep] = React.useState(mode === "deny" ? 4 : 0);
  const [open, setOpen] = React.useState(true);
  const [receiptDrupalOpen, setReceiptDrupalOpen] = React.useState(false);
  const [confirmation, setConfirmation] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [scaSessionCode, setScaSessionCode] = React.useState(null);
  const [scaRequestId, setScaRequestId] = React.useState(null);
  const [type, setType] = React.useState(null);
  const { showLoader, hideLoader } = useLoader();

  const uid = drupalSettings?.user?.uid ?? drupalSettings?.user?.user_id;
  const approvePayloadRef = React.useRef(null);

  const handleClose = React.useCallback(() => {
    setOpen(false);
    if (typeof window.destoryReactApp === "function") {
      window.destoryReactApp();
    }
    if (typeof jQuery !== "undefined" && jQuery(".view-banking").length) {
      jQuery(".view-banking").trigger("RefreshView");
    }
  }, []);

  // Fetch confirmation when opening in authorise mode
  React.useEffect(() => {
    if (mode !== "authorise" || step !== 0 || !orderId) return;
    setLoading(true);
    bankTransferConfirmationApi({ order_id: orderId })
      .then((data) => {
        setConfirmation(data);
        setStep(1);
      })
      .catch(() => {
        setStep(-1);
      })
      .finally(() => setLoading(false));
  }, [mode, orderId, step]);

  // Auto-close popup after success (step 3) or denied (step 5)
  // React.useEffect(() => {
  //   if (step !== 3 && step !== 5) return;
  //   const timer = setTimeout(handleClose, 2000);
  //   return () => clearTimeout(timer);
  // }, [step, handleClose]);

  const setOpenWithCleanup = (v) => {
    setOpen(v);
    if (v === false) {
      if (typeof window.destoryReactApp === "function") window.destoryReactApp();
      if (typeof jQuery !== "undefined" && jQuery(".view-banking").length) {
        jQuery(".view-banking").trigger("RefreshView");
      }
    }
  };

  const handleAuthorizeYes = () => {
    if (!confirmation || !uid) return;
    setLoading(true);
    const payload = {
      order_id: orderId,
      uid,
      fee: confirmation.fee ?? 0,
    };
    if (confirmation.quote_id) {
      payload.quote_id = confirmation.quote_id;
      payload.sell_amount = confirmation.sell_amount;
      payload.fx_rate = confirmation.fx_rate;
    }
    approvePayloadRef.current = payload;
    bankTransferApproveApi(payload)
      .then((res) => {
        if (res?.insufficient_funds) {
          setStep(6);
          return;
        }
        if (res?.result === true) {
          setStep(3);
          return;
        }
        if (res?.sca_required && res?.sca_session_code) {
          setScaSessionCode(res.sca_session_code);
          if (res.request_id) setScaRequestId(res.request_id);
          if (res.type) setType(res.type);
          setStep(2);
          return;
        }
        if (res?.sca_required && !res?.sca_session_code) {
          setStep(-1);
          return;
        }
        const msg = (res?.message || "").toLowerCase();
        if (msg.includes("not enough funds") || msg.includes("insufficient")) {
          setStep(-2);
        } else {
          setStep(-1);
        }
      })
      .catch((err) => {
        const msg = (err?.response?.data?.message || err?.message || "").toLowerCase();
        if (msg.includes("not enough funds") || msg.includes("insufficient")) {
          setStep(-2);
        } else {
          setStep(-1);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleQueueAuthorizationYes = () => {
    const base = approvePayloadRef.current;
    if (!base || !uid) return;
    setLoading(true);
    bankTransferApproveApi({ ...base, queue_authorization: true })
      .then((res) => {
        if (res?.insufficient_funds && res?.queued) {
          setStep(7);
        }
        else {
          setStep(-1);
        }
      })
      .catch(() => setStep(-1))
      .finally(() => setLoading(false));
  };

  const handleScaSuccess = (payload) => {
    const token = payload?.token;

    if (!token || !uid) {
      setStep(-1);
      return;
    }
    setLoading(true);
    const body = {
      order_id: orderId,
      uid,
      fee: confirmation?.fee ?? 0,
      sca_token: token,
    };
    if (scaRequestId) body.request_id = scaRequestId;
    if (type) body.type = type;
    if (confirmation?.quote_id) {
      body.quote_id = confirmation.quote_id;
      body.sell_amount = confirmation.sell_amount;
      body.fx_rate = confirmation.fx_rate;
    }
    approvePayloadRef.current = body;
    bankTransferApproveApi(body)
      .then((res) => {
        if (res?.queued) {
          setStep(6);
          return;
        }
        if (res?.result === true) {
          setStep(3);
        } else {
          setStep(-1);
        }
      })
      .catch(() => setStep(-1))
      .finally(() => setLoading(false));
  };

  const handleDenyYes = () => {
    if (!uid) return;
    setLoading(true);
    bankTransferDenyApi({ order_id: orderId, uid })
      .then((res) => {
        if (res?.result === true) {
          setStep(5);
        } else {
          setStep(-1);
        }
      })
      .catch(() => setStep(-1))
      .finally(() => setLoading(false));
  };

  const setStepOrClose = (s) => {
    if (s === "close") handleClose();
    else setStep(s);
  };

  const getStepComponent = () => {
    switch (step) {
      case 0:
        return (
          <div className="popup-body-content" style={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: "150px",
            position: 'relative',
          }}>
            <div class="ajax-progress ajax-progress-throbber"><div class="throbber"></div></div>
          </div>
        );
      case 1:
        return (
          <AuthorizeConfirm
            confirmation={confirmation}
            setStep={setStepOrClose}
            onYes={handleAuthorizeYes}
            loading={loading}
            orderId={orderId}
            onReceiptDrupalOpenChange={setReceiptDrupalOpen}
          />
        );
      case 2:
        return (
          <ScaStep
            scaSessionCode={scaSessionCode}
            onVerificationSucceed={handleScaSuccess}
            onVerificationFailed={() => setStep(-1)}
            onCancel={handleClose}
            loading={loading}
          />
        );
      case 3:
        return <SuccessStep setStep={setStepOrClose} onClose={handleClose} />;
      case -1:
        return <ErrorStep />;
      case -2:
        return <ErrorInsufficientFundsStep />;
      case 4:
        return (
          <DenyConfirm
            setStep={setStepOrClose}
            onYes={handleDenyYes}
            loading={loading}
          />
        );
      case 5:
        return <RequestDeniedStep setStep={setStepOrClose} onClose={handleClose} />;
      case 6:
        return (
          <InsufficientFundsQueueStep
            onYes={handleQueueAuthorizationYes}
            onNo={() => setStep(1)}
            loading={loading}
          />
        );
      case 7:
        return (
          <QueuedAuthorizationSuccessStep onClose={handleClose} />
        );
      default:
        return null;
    }
  };

  const modalClass =
    step === 3 || step === 7
      ? "authorise-bank-transfer-popup has-success"
      : step === 5
        ? "authorise-bank-transfer-popup has-denied"
        : step === -1 || step === -2
          ? "authorise-bank-transfer-popup has-error"
          : "authorise-bank-transfer-popup";

  return (
    <GeneralModal
      open={open}
      setOpen={setOpenWithCleanup}
      showCloseIcon={true}
      className={modalClass}
      focusTrapped={!receiptDrupalOpen}
      blockScroll={!receiptDrupalOpen}
      styles={receiptDrupalOpen ? { root: { display: "none" } } : undefined}
    >
      {getStepComponent()}
    </GeneralModal>
  );
}
